import MarkerManager from "./markerManager.js";
import CardPanel from "./cardPanel.js";
import {
    DENSEST_RADIUS_METERS,
    MAP_DEFAULT_CONFIG,
    MAP_FIT_BOUNDS_PADDING,
} from "./consts.js";
import { findDensestPoint } from "./densestPoint.js";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export class GoogleMap {
    constructor(params, stateManager) {
        this.config = {
            ...this.defaults(),
            ...params,
        };

        this.stateManager = stateManager;
        this.markerManager = null;
        this.cardPanel = null;
        this.map = null;
        this.projectionOverlay = null;

        // 地図の準備完了を外から待てるようにする（キーワード検索が読み込み前に走るため）
        this.ready = this.init();
    }

    defaults() {
        return {
            api_key: null,
            map_id: null,
            // 既定の表示位置。未指定なら MAP_DEFAULT_CONFIG のまま
            default_center: null,
            default_zoom: null,
            // 一覧から引き継いだ市区町村を囲む矩形（{north, east, south, west}）。
            // あれば既定の表示位置よりこちらを優先する
            fit_bounds: null,
            // 既定の中心を条件（駅）で決めたか。false なら県庁所在地の保険なので、
            // 受け取った物件の密集地点へ寄せ直す
            center_fixed: false,
        };
    }

    async init() {
        const { root, propertyContainer, card } = this.stateManager.elements;
        if (!this.config.api_key || !this.config.map_id) return;
        if (!root || !propertyContainer || !card) {
            console.error(
                "地図ページの要素が見つからないため地図を初期化できません（.js-search-map / .js-search-map__card / .js-search-map__property-container）"
            );
            return;
        }

        try {
            await this.loadMap();
        } catch (error) {
            console.error("地図の初期化に失敗しました:", error);
            this.stateManager.showError(
                "地図を表示できませんでした。時間をおいて再度お試しください。"
            );
        }
    }

    async loadMap() {
        setOptions({
            key: this.config.api_key,
            v: "weekly",
            // 国内向けサイトのため地図ラベルと地名解決を日本語・日本基準に固定する
            language: "ja",
            region: "JP",
        });

        // 物件は県ぶん全部を1回だけ受け取る。地図ライブラリの読み込みと並行して取り、
        // 中心（密集地点）を決めてから地図を作る（作ってから寄せると開いた直後に地図が跳ぶ）
        const [{ Map }, properties] = await Promise.all([
            importLibrary("maps"),
            this.stateManager.fetchProperties(),
        ]);

        this.initializeMap(Map, this.resolveInitialCenter(properties));

        this.cardPanel = new CardPanel({
            mapRoot: this.stateManager.elements.root,
            cardRoot: this.stateManager.elements.card,
            cardInner: this.stateManager.elements.propertyContainer,
        });
        await this.setupProjection();

        this.markerManager = new MarkerManager(
            this.map,
            this.stateManager,
            this.cardPanel
        );
        await this.markerManager.init();

        // カードを閉じたらピンの選択表示も解除する
        this.cardPanel.setOnClose(() => this.markerManager.clearActiveMarker());

        // 引き継いだエリアへ寄せてからピンを立てる。同座標のずらし幅は
        // ピンを立てたときのズームで決まるので、寄せた後のズームで並べたい
        await this.applyInitialBounds();

        this.markerManager.setProperties(properties);

        this.setupMapEventListeners();
    }

    /**
     * 地図を作るときの中心。
     *
     * 一覧から市区町村（矩形）や駅（center_fixed）を引き継いだときは、その位置が正。
     * どちらも無い（県TOPから開いた等）ときだけ、受け取った物件がいちばん密集している
     * 地点にする。物件が無ければサーバーの既定（県庁所在地）のまま。
     * ※矩形があるときは applyInitialBounds() が寄せ直すので、ここは既定のままでよい
     */
    resolveInitialCenter(properties) {
        if (this.config.fit_bounds || this.config.center_fixed) {
            return this.config.default_center;
        }

        const points = (properties ?? [])
            .map((property) => ({
                lat: Number(property?.geopoint?.lat),
                lng: Number(property?.geopoint?.lng),
            }))
            .filter(
                (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)
            );

        return (
            findDensestPoint(points, DENSEST_RADIUS_METERS) ??
            this.config.default_center
        );
    }

    initializeMap(Map, center) {
        this.map = new Map(this.stateManager.elements.root, {
            ...MAP_DEFAULT_CONFIG,
            // 都道府県版では物件の密集地点（無ければ県庁所在地）を既定位置にする。
            // 総合版・エリア未確定では meta が無いので MAP_DEFAULT_CONFIG のまま
            ...(center ? { center } : {}),
            ...(this.config.default_zoom
                ? { zoom: this.config.default_zoom }
                : {}),
            mapId: this.config.map_id,
        });
    }

    /**
     * 一覧から引き継いだ市区町村を囲む矩形へ地図を寄せる。
     *
     * エリア軸は地図検索の絞り込みには使えない（SearchApiTrait::apiMap のコメント参照）ので、
     * 「最初にどこを映すか」で引き継ぐ。どこを映すかの判断はサーバー側
     * （App\Support\Map\MapViewportResolver）が済ませていて、ここは寄せるだけ。
     * 矩形が無いときは初期化時の中心・ズーム（resolveInitialCenter）のままにする。
     *
     * fitBounds 直後の getZoom() はまだ移動前の値を返しうるため、
     * 反映が終わった合図の idle を待ってから戻る。待たずに戻ると、
     * このあとのピンの並べ（同座標のずらし幅）が「寄せる前のズーム」で決まってしまう。
     * ※ idle が来ない事故で読み込み中のまま固まらないよう、保険のタイムアウトを置く
     */
    async applyInitialBounds() {
        if (!this.map || !this.config.fit_bounds) return;

        await new Promise((resolve) => {
            let listener = null;
            const done = () => {
                if (!listener) return;
                listener.remove();
                listener = null;
                resolve();
            };

            listener = this.map.addListener("idle", done);
            setTimeout(done, 2000);

            this.map.fitBounds(
                this.config.fit_bounds,
                MAP_FIT_BOUNDS_PADDING
            );
        });
    }

    /**
     * 緯度経度 → 地図コンテナ内ピクセルの変換手段を用意する。
     * Google Maps はこの変換を OverlayView の projection 経由でしか公開していないため、
     * 描画を持たない空のオーバーレイを1つだけ地図に載せて projection を借りる。
     */
    async setupProjection() {
        const { OverlayView } = await importLibrary("maps");
        const { LatLng } = await importLibrary("core");

        // OverlayView は継承前提のAPIなので、何も描画しない最小の派生クラスを1つだけ作る
        class ProjectionOverlay extends OverlayView {
            onAdd() {}
            draw() {}
            onRemove() {}
        }

        this.projectionOverlay = new ProjectionOverlay();
        this.projectionOverlay.setMap(this.map);

        this.cardPanel.setProjector(({ lat, lng }) => {
            // 初回 draw 前は projection が null。呼び出し側で未確定として扱う
            const projection = this.projectionOverlay.getProjection();
            if (!projection) return null;
            return projection.fromLatLngToContainerPixel(new LatLng(lat, lng));
        });
    }

    setupMapEventListeners() {
        // 物件は開いたときに受け取り済みなので取り直さない。ズームが変わっていれば
        // 同座標のずらし幅だけ並べ直し、開いているカードの位置も地図に追従させる
        this.map.addListener("idle", () => {
            this.markerManager.relayoutIfZoomChanged();
            this.cardPanel.reposition();
        });

        // ズーム中はピンの位置が変わるため開いているカードを追従させる
        this.map.addListener("zoom_changed", () => this.cardPanel.reposition());

        // 地図の余白クリックでカードを閉じる
        this.map.addListener("click", () =>
            this.cardPanel.handleMapBackgroundClick()
        );
    }
}

export default GoogleMap;
